import { SEO } from '../components/seo/SEO';
import { ArrowRight } from 'lucide-react';
import BankTransferDetails from '../components/common/BankTransferDetails';
import QuickPayments from '../components/common/QuickPayments';
import { NeededItems } from '../components/support/NeededItems';
import { PayUPayment } from '../components/payments';
import { getBreadcrumbSchema } from '../lib/jsonLd';

export function SupportPage() {
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Strona główna', url: 'https://znajdki.pl/' },
    { name: 'Wsparcie', url: 'https://znajdki.pl/wsparcie' }
  ]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <SEO 
        title="Jak pomóc zwierzętom - Wsparcie Fundacji Znajdki"
        description="Wesprzyj bezdomne zwierzęta. Wpłać darowiznę, zostań wolontariuszem lub domem tymczasowym. Każda pomoc ma znaczenie! Fundacja Znajdki."
        url="/wsparcie"
        image="/images/animals/fundacja-znajdki-zwierzeta-001.webp"
        jsonLd={breadcrumbSchema}
      />

      {/* Hero Section */}
      <div className="text-center mb-12">
        <h1 className="text-accessible-gray-900 dark:text-white high-contrast:text-white mb-6">
          Wesprzyj nas finansowo
        </h1>
        <p className="body-large max-w-3xl mx-auto">
          Twoje wsparcie pomaga nam zapewnić opiekę, leczenie i bezpieczny dom dla bezdomnych zwierząt.
          Każda złotówka ma znaczenie!
        </p>
      </div>

      {/* W prostym języku */}
      <section className="mb-8">
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
          <h2 className="heading-4 no-margin">W skrócie</h2>
          <p className="body-medium no-margin mt-2">
            Możesz przelać środki za pomocą BLIK na telefon, PayPal, PayU (karta, przelew natychmiastowy) lub tradycyjnym przelewem. Każda wpłata pomaga.
          </p>
        </div>
      </section>

      {/* Główna treść w jednej kolumnie */}
      <div className="space-y-12">
        {/* Szybkie płatności i dane przelewów */}
        <QuickPayments columns="two" />
        
        {/* PayU Payment Section */}
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-2">
              Wpłata online przez PayU
            </h2>
            <p className="body-medium">
              Bezpieczna płatność kartą, BLIKIEM lub przelewem natychmiastowym
            </p>
          </div>
          <PayUPayment />
        </div>

        <BankTransferDetails columns="one" />

        {/* Dodatkowe informacje */}
        <div className="space-y-8">
          {/* How We Use the Funds */}
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-6">
              Na co przeznaczamy środki?
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-2">
                  Opieka weterynaryjna
                </h3>
                <p className="body-medium">
                  Leczenie, szczepienia, zabiegi chirurgiczne i regularne kontrole zdrowia.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-2">
                  Wyżywienie
                </h3>
                <p className="body-medium">
                  Wysokiej jakości karma i specjalistyczne diety dla zwierząt o szczególnych potrzebach.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-2">
                  Utrzymanie schroniska
                </h3>
                <p className="body-medium">
                  Ogrzewanie, środki czystości, naprawy i modernizacje pomieszczeń.
                </p>
              </div>
            </div>
          </div>

          {/* Other Ways to Help */}
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-6">
              Inne formy wsparcia
            </h2>
            <div className="space-y-4">
              <a href="/przekaz-podatek" className="block p-4 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-600 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="label-large" title="Przekaż 1.5% podatku na pomoc zwierzętom">
                    Przekaż 1.5% podatku
                  </span>
                  <ArrowRight className="w-5 h-5 text-accessible-gray-500" />
                </div>
              </a>
              <a href="/dom-tymczasowy" className="block p-4 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-600 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="label-large" title="Zostań domem tymczasowym dla zwierzaka">
                    Dom tymczasowy
                  </span>
                  <ArrowRight className="w-5 h-5 text-accessible-gray-500" />
                </div>
              </a>
              <a href="/wolontariat" className="block p-4 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-600 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="label-large" title="Dołącz do naszego zespołu wolontariuszy">
                    Zostań wolontariuszem
                  </span>
                  <ArrowRight className="w-5 h-5 text-accessible-gray-500" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
      <NeededItems />
    </div>
  );
}